import { ReactNode, useEffect, useRef } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import {
  ScaffolderFieldError,
  ScaffolderInputHeader,
} from '../../../components';
import * as doc from '../infoDoc';
import { useStyles as useSharedStyles } from '../styles';
import { Cluster, ClusterConfiguration } from '../types';
import { clusterInputErrors, namespaceInputErrors } from '../validation';

const useStyles = makeStyles()({
  namespacePrefix: {
    opacity: 0.6,
    whiteSpace: 'nowrap',
  },
});

export interface ClusterPickerProps {
  onChange: (value: ClusterConfiguration | null) => void;
  clusters: Cluster[];
  value: ClusterConfiguration;
  errors: string[];
  enableAppSetSupport?: boolean;
}

const getNamespacePrefix = (allowedNamespaces: string[]): string => {
  const first = allowedNamespaces[0];
  if (!first) return '';
  return first.endsWith('*') ? first.slice(0, -1) : '';
};

export const ClusterPicker = (props: ClusterPickerProps) => {
  const {
    onChange,
    value,
    clusters,
    errors,
    enableAppSetSupport = false,
  } = props;
  const { classes } = useStyles();
  const { classes: sharedClasses } = useSharedStyles();
  const clusterRef = useRef<HTMLDivElement>(null);
  const namespaceRef = useRef<HTMLDivElement>(null);

  const selectedCluster =
    clusters.find(cluster => cluster.name === value?.name) || null;

  const clusterDisabled = clusters.length === 0;
  const namespaceDisabled = value.allowedNamespaces.length === 0;

  const namespacePrefix =
    !enableAppSetSupport && value.kaapTenant
      ? getNamespacePrefix(value.allowedNamespaces)
      : '';
  const namespaceSuffix =
    namespacePrefix && value.namespace.startsWith(namespacePrefix)
      ? value.namespace.slice(namespacePrefix.length)
      : value.namespace;

  const clusterError = errors.length > 0 ? clusterInputErrors(value) : '';
  const namespaceError =
    errors.length > 0
      ? namespaceInputErrors(
          value.namespace,
          value.allowedNamespaces,
          enableAppSetSupport,
        )
      : '';

  useEffect(() => {
    if (clusterError) {
      clusterRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    if (namespaceError) {
      namespaceRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [clusterError, namespaceError]);

  const handleClusterChange = (_: unknown, v: Cluster | null = null) => {
    const prefix =
      !enableAppSetSupport && v?.kaapTenant
        ? getNamespacePrefix(v?.namespaces || [])
        : '';
    onChange({
      name: v?.name || '',
      id: v?.id || '',
      server: v?.server || '',
      allowedNamespaces: v?.namespaces || [],
      namespace: prefix,
      ...(v?.kaapTenant && { kaapTenant: { name: v.kaapTenant.name } }),
    });
  };

  const handleNamespaceChange = (suffix: string) => {
    onChange({ ...value, namespace: namespacePrefix + suffix });
  };

  return (
    <Box>
      <Box className={sharedClasses.inputContainer}>
        <ScaffolderInputHeader
          title="Cluster Details"
          description="Select the cluster and namespace."
          infoDoc={doc.CLUSTER_INFO}
        />
      </Box>
      <Box className={sharedClasses.inputContainer}>
        <Autocomplete
          size="small"
          value={selectedCluster}
          disabled={clusterDisabled}
          onChange={handleClusterChange}
          options={clusters}
          getOptionLabel={option => option.name}
          isOptionEqualToValue={(option, selected) =>
            option.name === selected.name
          }
          renderInput={params => {
            let clusterHelperText: ReactNode;
            if (clusterDisabled) {
              clusterHelperText =
                'Select a project first to see available clusters';
            } else if (clusterError) {
              clusterHelperText = (
                <ScaffolderFieldError message={clusterError} />
              );
            } else {
              clusterHelperText =
                'Select the cluster to deploy the application to.';
            }
            return (
              <TextField
                {...params}
                label="Cluster Name"
                ref={clusterRef}
                margin="none"
                variant="outlined"
                required
                error={Boolean(clusterError)}
                helperText={clusterHelperText}
              />
            );
          }}
          renderOption={({ key, ...optionProps }, option) => (
            <li key={key} {...optionProps}>
              <Box>
                <Typography variant="body1" noWrap style={{fontWeight: 600}}>
                  {option.name}
                </Typography>
                {option.name && (
                  <Typography variant="caption" noWrap color="text.secondary">
                    Cluster endpoint: {option.server}
                  </Typography>
                )}
              </Box>
            </li>
          )}
        />
      </Box>
      <Box className={sharedClasses.inputContainer}>
        <TextField
          label="Namespace"
          variant="outlined"
          margin="none"
          size="small"
          ref={namespaceRef}
          fullWidth
          required
          disabled={namespaceDisabled}
          value={namespaceSuffix}
          onChange={e => handleNamespaceChange(e.target.value)}
          error={Boolean(namespaceError)}
          slotProps={{
            htmlInput: enableAppSetSupport
              ? undefined
              : { maxLength: 63 - namespacePrefix.length },
            input: namespacePrefix
              ? {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="subtitle1"
                        className={classes.namespacePrefix}
                      >
                        {namespacePrefix}
                      </Typography>
                    </InputAdornment>
                  ),
                }
              : undefined,
          }}
          helperText={(() => {
            if (namespaceError) {
              return <ScaffolderFieldError message={namespaceError} />;
            }
            if (value.allowedNamespaces.length > 0) {
              return (
                <span>
                  Values must match:{' '}
                  <strong>{value.allowedNamespaces.join(', ')}</strong>
                </span>
              );
            }
            return 'The namespace to deploy the application into.';
          })()}
        />
      </Box>
    </Box>
  );
};
